import { useState } from 'react';
import { useNavigate } from 'react-router';
import { FEEDBACK_MAX_LENGTH } from '@debtbot/shared';
import { useSendFeedback } from '../api/hooks';
import { ApiError } from '../api/errors';
import { ErrorBanner } from '../components/ErrorBanner';
import { MainButtonFallback } from '../components/MainButtonFallback';
import { useToast } from '../components/Toast';
import { useT } from '../i18n';
import { haptic } from '../telegram/haptic';
import { useClosingConfirmation, useMainButton } from '../telegram/ui';

/** Предложение по улучшению: бэкенд пересылает текст разработчику в Telegram. */
export function FeedbackScreen() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const send = useSendFeedback();
  const [text, setText] = useState('');
  const valid = text.trim().length > 0;

  useClosingConfirmation(valid);

  const submit = async () => {
    if (!valid || send.isPending) return;
    try {
      await send.mutateAsync({ text: text.trim() });
      haptic.success();
      toast({ text: t.feedback.sent });
      void navigate(-1);
    } catch {
      haptic.error();
    }
  };

  useMainButton({
    text: t.feedback.send,
    enabled: valid,
    loading: send.isPending,
    onClick: () => void submit(),
  });

  const rateLimited = send.error instanceof ApiError && send.error.code === 'RATE_LIMITED';

  return (
    <main className="min-h-dvh bg-bg text-text px-4 py-4 pb-24 flex flex-col gap-4">
      <h1 className="text-xl font-semibold">{t.feedback.title}</h1>
      <p className="text-hint">{t.feedback.body}</p>
      {rateLimited ? (
        <div role="alert" className="rounded-xl bg-secondary-bg px-4 py-3 text-warning">
          {t.feedback.tooOften}
        </div>
      ) : (
        send.isError && <ErrorBanner error={send.error} />
      )}
      <textarea
        data-testid="feedback-input"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, FEEDBACK_MAX_LENGTH))}
        placeholder={t.feedback.placeholder}
        rows={6}
        autoFocus
        className="resize-none rounded-xl bg-secondary-bg px-4 py-3 outline-none placeholder:text-hint"
      />
      <div className="text-right text-xs text-hint tabular-nums">
        {text.length} / {FEEDBACK_MAX_LENGTH}
      </div>
      <MainButtonFallback
        text={t.feedback.send}
        disabled={!valid || send.isPending}
        onClick={() => void submit()}
      />
    </main>
  );
}
